import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

const nigerianStates = [
  'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 
  'Benue', 'Borno', 'Cross River', 'Delta', 'Ebonyi', 'Edo',
  'Ekiti', 'Enugu', 'FCT', 'Gombe', 'Imo', 'Jigawa',
  'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara',
  'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun',
  'Oyo', 'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'
];

const stateCities = {
  'Lagos': ['Lagos Island', 'Ikeja', 'Victoria Island', 'Lekki'],
  'FCT': ['Garki', 'Wuse', 'Maitama', 'Asokoro'],
  'Rivers': ['Port Harcourt', 'Bonny', 'Eleme', 'Okrika'],
  // Add more states and cities as needed
};

const SearchBar = ({ fadeInRightVariants, buttonVariants, searchRef, isSearchInView }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [filteredStates, setFilteredStates] = useState(nigerianStates);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  const stateInputRef = useRef(null);
  const cityInputRef = useRef(null);
  const stateDropdownRef = useRef(null);
  const cityDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (stateInputRef.current && !stateInputRef.current.contains(event.target) &&
          (!stateDropdownRef.current || !stateDropdownRef.current.contains(event.target))) {
        setShowStateDropdown(false);
      }
      
      if (selectedState && cityInputRef.current && !cityInputRef.current.contains(event.target) &&
          (!cityDropdownRef.current || !cityDropdownRef.current.contains(event.target))) {
        setShowCityDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedState]);

  const handleStateSearch = (e) => {
    const query = e.target.value;
    setSelectedState(query);
    setShowStateDropdown(true); // Ensure dropdown remains open while typing
    
    const filtered = nigerianStates.filter(state => 
      state.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredStates(filtered);
  };

  const handleStateSelect = (state) => {
    setSelectedState(state);
    setShowStateDropdown(false);
    setShowCityDropdown(false);
    setSelectedCity('');
  };

  const handleCitySelect = (city) => {
    setSelectedCity(city);
    setShowCityDropdown(false);
  };
  return (
    <motion.div
      ref={searchRef}
      variants={fadeInRightVariants}
      initial="hidden"
      animate={isSearchInView ? "visible" : "hidden"}
      className="bg-white rounded-xl shadow-sm p-6 max-sm:p-0 mb-12"
    >
      <div className="flex max-sm:flex-col md:flex-row items-center bg-[#F8F8F8] max-sm:bg-white overflow-visible">
        {/* Search Events Input */}
        <div className="flex-1 relative rounded-[32px] w-full bg-[#F8F8F8]">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-[#888888]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search Events..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-transparent focus:outline-none border-none"
          />
        </div>
        
        <div className="h-8 w-px bg-gray-300 hidden md:block"></div>
        
        {/* State Selection */}
        <div className="flex-1 relative rounded-[32px] w-full bg-[#F8F8F8] max-sm:mt-5" ref={stateInputRef}>
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-[#888888]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Enter State"
            value={selectedState}
            onChange={handleStateSearch}
            onFocus={() => {
              setShowStateDropdown(true);
              setFilteredStates(nigerianStates);
            }}
            className="w-full pl-10 pr-4 py-3 bg-transparent focus:outline-none border-none"
          />
          
          {showStateDropdown && (
            <div 
              ref={stateDropdownRef}
              className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
            >
              {filteredStates.length > 0 ? (
                filteredStates.map((state) => (
                  <div
                    key={state}
                    className="px-4 text-[#888888] py-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => handleStateSelect(state)}
                  >
                    {state}
                  </div>
                ))
              ) : (
                <div className="px-4 py-2  ">No states found</div>
              )}
            </div>
          )}
        </div>
        
        {/* City Dropdown */}
        {selectedState && (
          <>
            <div className="h-8 w-px bg-gray-300 hidden md:block"></div>
            <div className="flex-1 relative rounded-[32px] max-sm:mt-5 w-full bg-[#F8F8F8]" ref={cityInputRef}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-[#888888]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Select City"
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                onFocus={() => setShowCityDropdown(true)}
                className="w-full pl-10 pr-4 py-3 bg-transparent focus:outline-none border-none"
              />
              
              {showCityDropdown && stateCities[selectedState] && (
                <div 
                  ref={cityDropdownRef}
                  className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
                >
                  {stateCities[selectedState]
                    .filter(city => city.toLowerCase().includes(selectedCity.toLowerCase()))
                    .map((city) => (
                      <div
                        key={city}
                        className="px-4 text-[#888888] py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => handleCitySelect(city)}
                      >
                        {city}
                      </div>
                    ))}
                </div>
              )}
            </div>
          </>
        )}
        
        <div className="h-8 w-px bg-gray-300 hidden md:block"></div>
        
        <motion.button
          variants={buttonVariants}
          whileHover="hover"
          whileTap="tap"
          className="px-6 py-3 bg-[#1E1E1E] max-sm:w-full max-sm:mt-5 text-white font-medium rounded-full h-full"
        >
          Find event
        </motion.button>
      </div>
    </motion.div>
  );
};

export default SearchBar;